<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useTour } from '../composables/useTour'
import { useBreakpoints } from '@/composables/useBreakpoints'
import { useNotification } from '@/composables/useNotification'
import { useSpotlight } from '../engine/useSpotlight'
import { computePopoverPosition } from '../engine/usePopoverPosition'
import { TOUR_CONFIG } from '../config/tour.config'
import type { TourSpotlightShape } from '../types/tour.types'
import TourSpotlight from './TourSpotlight.vue'
import TourPopover from './TourPopover.vue'
import TourMobileSheet from './TourMobileSheet.vue'
import ExplorePanel from './ExplorePanel.vue'

const SKIP_MESSAGE =
  'Whenever you need it, click the help button on this screen. Or just ask the AI directly — there\'s a good chance it can do it for you, without you needing to learn the whole process.'

const tour = useTour()
const { isMobile } = useBreakpoints()
const notify = useNotification()

const shape = computed<TourSpotlightShape>(() => tour.currentStep.value?.spotlightShape ?? 'auto')
const padding = computed(() => tour.currentStep.value?.spotlightPadding ?? TOUR_CONFIG.spotlightPadding)

const { rect } = useSpotlight(tour.targetEl, shape, padding)

const showOverlay = computed(() => tour.isRunning.value)
const showPopover = computed(() => tour.phase.value === 'running')

// --- Popover positioning ---
const popoverWrap = ref<HTMLElement | null>(null)
const popoverSize = ref({ width: 360, height: 220 })
const viewport = ref({ width: 1280, height: 800 })

let ro: ResizeObserver | null = null
watch(popoverWrap, (el) => {
  ro?.disconnect()
  ro = null
  if (el) {
    ro = new ResizeObserver(() => {
      popoverSize.value = { width: el.offsetWidth, height: el.offsetHeight }
    })
    ro.observe(el)
    popoverSize.value = { width: el.offsetWidth, height: el.offsetHeight }
  }
})

function syncViewport(): void {
  viewport.value = { width: window.innerWidth, height: window.innerHeight }
}

const position = computed(() =>
  computePopoverPosition(
    rect.value,
    popoverSize.value,
    tour.currentStep.value?.placement ?? 'auto',
    viewport.value,
  ),
)

const popoverStyle = computed(() => ({
  position: 'fixed' as const,
  top: `${position.value.top}px`,
  left: `${position.value.left}px`,
  zIndex: TOUR_CONFIG.zIndex.popover,
}))

// --- Feedback via the existing toast system (design-system consistent) ---
watch(
  () => tour.skipNoticeVisible.value,
  (v) => {
    if (v) {
      notify.info(SKIP_MESSAGE, { title: 'Training paused' })
      tour.dismissSkipNotice()
    }
  },
)
watch(
  () => tour.lastCompletedTourId.value,
  (v) => {
    if (v) {
      notify.success('Screen complete — nice work!')
      tour.clearCompletion()
    }
  },
)

const TRAINING_OFF_MESSAGE =
  "Tours won't open on their own any more. To see a screen's tour, or to turn training mode back on, use the help button at the top."

function onDisableTraining(): void {
  tour.disableTrainingMode()
  notify.info(TRAINING_OFF_MESSAGE, { title: 'Training mode off' })
}

function onExecuteAi(prompt: string): void {
  tour.stop()
  tour.executeWithAI(prompt)
}

// Escape closes the running tour (same as the popover's X — a quiet close, not the "ask the
// AI" Skip). The overlay is modal, so pressing Escape is the expected way out.
function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape' && tour.isRunning.value) {
    e.preventDefault()
    tour.close()
  }
}

onMounted(() => {
  syncViewport()
  window.addEventListener('resize', syncViewport)
  window.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  window.removeEventListener('resize', syncViewport)
  window.removeEventListener('keydown', onKeydown)
  ro?.disconnect()
})
</script>

<template>
  <Teleport to="body">
    <div v-if="showOverlay" class="tour-root">
      <TourSpotlight :rect="rect" />

      <template v-if="showPopover && tour.currentStep.value">
        <TourMobileSheet
          v-if="isMobile"
          :step="tour.currentStep.value"
          :step-index="tour.stepIndex.value"
          :total-steps="tour.totalSteps.value"
          :is-first="tour.isFirstStep.value"
          :is-last="tour.isLastStep.value"
          :tour-title="tour.activeTour.value?.title ?? ''"
          @next="tour.next()"
          @prev="tour.prev()"
          @skip="tour.skip()"
          @close="tour.close()"
          @execute-ai="onExecuteAi"
          @disable-training="onDisableTraining"
        />
        <div v-else ref="popoverWrap" class="tour-root__popover" :style="popoverStyle">
          <TourPopover
            :step="tour.currentStep.value"
            :step-index="tour.stepIndex.value"
            :total-steps="tour.totalSteps.value"
            :is-first="tour.isFirstStep.value"
            :is-last="tour.isLastStep.value"
            :placement="position.placement"
            :pinned="position.pinned"
            :tour-title="tour.activeTour.value?.title ?? ''"
            @next="tour.next()"
            @prev="tour.prev()"
            @skip="tour.skip()"
            @close="tour.close()"
            @repeat="tour.repeatStep()"
            @restart="tour.restart()"
            @execute-ai="onExecuteAi"
            @disable-training="onDisableTraining"
          />
        </div>
      </template>
    </div>
  </Teleport>

  <ExplorePanel />
</template>

<style scoped lang="scss">
.tour-root__popover {
  transition:
    top 200ms cubic-bezier(0.16, 1, 0.3, 1),
    left 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

@media (prefers-reduced-motion: reduce) {
  .tour-root__popover {
    transition: none;
  }
}
</style>
